import { getDefaultAvatar } from "@repo/fika-design-system/avatar"
import type { Locale } from "@repo/fika-shared/src/locale"
import { parseLocale } from "@repo/fika-shared/src/locale"
import { translate } from "@repo/fika-shared/src/translate"
import { A } from "@solidjs/router"
import { createQuery } from "@urql/solid"
import { type Accessor, createMemo, For, Show } from "solid-js"
import { Box } from "@/designSystem/components/box"
import { Icon } from "@/designSystem/components/icon"
import {
  PageInfoFragment,
  Pagination,
} from "@/designSystem/components/pagination"
import { ResponsiveImage } from "@/designSystem/components/responsive-image"
import { Text } from "@/designSystem/components/text"
import { MainLayout } from "@/designSystem/layouts/main-layout"
import { Navigation } from "@/designSystem/patterns/navigation"
import { createFragment, type FragmentOf, graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import { createSubscriptionsRouter } from "@/routes/subscriptions-router"
import {
  cardClass,
  cardContentClass,
  cardHeaderClass,
  cardMainContentClass,
  mainClass,
  tierBadgeClass,
} from "./subscription-manage.css"

const SubscriptionManageCardFragment = graphql(`
  fragment SubscriptionManageCardFragment on Sub {
    id
    tier {
      id
      name
    }
    publication {
      id
      name
      title
      description
      logoUrl
      author {
        id
        authorName
        profileImage
      }
    }
    currentPeriodEnd
    billingInterval
  }
`)

const SubscriptionsQuery = graphql(
  `query Subs($after: String, $before: String, $first: Int, $last: Int) {
    subs(after: $after, before: $before, first: $first, last: $last) {
      edges {
        cursor
        node {
          ...SubscriptionManageCardFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }`,
  [PageInfoFragment, SubscriptionManageCardFragment],
)

type SubscriptionNode = FragmentOf<typeof SubscriptionManageCardFragment>

export function SubscriptionManage() {
  const { LL, locale } = useI18nContext()
  const { paths, manageParams, listParams } = createSubscriptionsRouter()
  const interfaceLocale = createMemo(() => parseLocale(locale()))

  const variables = createMemo(() => {
    const params = manageParams()
    if (params.before) {
      return { before: params.before, last: 20 }
    }
    return { after: params.after || undefined, first: 20 }
  })

  const [result] = createQuery({
    query: SubscriptionsQuery,
    variables,
    requestPolicy: "cache-and-network",
  })

  function buildUrl(page: { after: string; before: string }) {
    return paths(listParams()).manage(page).url
  }

  return (
    <MainLayout
      hue="indigo"
      header={
        <Navigation
          title={LL().subscriptions.manageTitle()}
          hue="indigo"
          buttons={[
            {
              label: LL().subscriptions.title(),
              href: paths(listParams()).url,
            },
          ]}
        />
      }
      query={result}
    >
      {data => {
        const subscriptions = createMemo(() => data?.subs)
        const subs = createMemo(() =>
          (subscriptions()?.edges ?? []).map(edge => edge.node),
        )
        const pageInfo = createMemo(() => subscriptions()?.pageInfo)

        return (
          <div class={mainClass}>
            <Show
              when={subs().length > 0}
              fallback={
                <Box padding="24" gap="16" alignItems="center">
                  <Icon name="newspaper" size="32" color="sand400" />
                  <Text color="sand600" fontSize="m">
                    {LL().settings.subscriptions.empty()}
                  </Text>
                </Box>
              }
            >
              <Box gap="16">
                <For each={subs()}>
                  {subscription => (
                    <SubscriptionManageCard
                      subscription={subscription}
                      interfaceLocale={interfaceLocale}
                    />
                  )}
                </For>
              </Box>
              <Pagination pageInfo={pageInfo()} buildUrl={buildUrl} />
            </Show>
          </div>
        )
      }}
    </MainLayout>
  )
}

function SubscriptionManageCard(props: {
  subscription: SubscriptionNode
  interfaceLocale: Accessor<Locale>
}) {
  const { LL } = useI18nContext()
  const { paths, listParams, manageParams } = createSubscriptionsRouter()
  const subscription = createFragment(
    SubscriptionManageCardFragment,
    () => props.subscription,
  )
  const href = createMemo(() =>
    paths(listParams()).manage(manageParams()).subscription({
      id: subscription().id,
      publicationName: subscription().publication.name,
    }),
  )
  const publicationTitle = createMemo(
    () =>
      translate(subscription().publication.title, props.interfaceLocale()) ??
      subscription().publication.name,
  )
  const publicationDescription = createMemo(() =>
    translate(subscription().publication.description, props.interfaceLocale()),
  )

  return (
    <A href={href()} class={cardClass}>
      <div class={cardHeaderClass}>
        <div class={cardMainContentClass}>
          <ResponsiveImage
            image={{
              url:
                subscription().publication.logoUrl ??
                getDefaultAvatar(subscription().publication.id),
              imageClass: "bigLogo",
            }}
            alt={publicationTitle() ?? subscription().publication.name ?? ""}
          />
          <div class={cardContentClass}>
            <Text fontSize="l" fontWeight="500" color="sand900">
              {publicationTitle()}
            </Text>
            <Show when={publicationDescription()}>
              {description => (
                <Text fontSize="s" color="sand600">
                  {description()}
                </Text>
              )}
            </Show>
            <Show when={subscription().publication.author.authorName}>
              {authorName => (
                <Text fontSize="s" color="sand500">
                  {LL().common.by()} {authorName()}
                </Text>
              )}
            </Show>
            <Show when={subscription().currentPeriodEnd && subscription().tier}>
              <Text fontSize="s" color="sand500">
                {LL().subscriptions.renewsAt({
                  date: new Date(
                    subscription().currentPeriodEnd!,
                  ).toLocaleDateString(),
                })}
              </Text>
            </Show>
          </div>
        </div>
        <Show when={subscription().tier}>
          {tier => (
            <div class={tierBadgeClass}>
              {translate(tier().name, props.interfaceLocale()) ?? tier().name}
            </div>
          )}
        </Show>
      </div>
    </A>
  )
}
